import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useHistory } from 'react-router';
import { debounce } from 'lodash';
import { useTrack } from '@hackler/react-sdk';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { CapaPcbServiceType } from '@generated/graphql';
import useQueryString from '@hooks/useQueryString';
import * as asset from '@assets';
import { requestEnumToString } from '@utils/enumToString';
import PannelLayout from '@routes/NewRequest/Layout/PannelLayout';
import RequestCommonLayout from '@routes/NewRequest/Layout/RequestCommonLayout';
import RequestBottomNav from '@routes/NewRequest/Layout/RequestBottomNav';
import {
  PcbRequestStageEnum,
  RequestQueryString
} from '@routes/NewRequest/types';
import RequestPreview from '@routes/NewRequest/RequestPreview';
import PCBProcessSelect from '../PCB/PCBProcessSelect';
import PCBAssembleStep from './CircuitAssemblyStep';
import PCBProduceStep from './CircuitFabricationStep';
import CuircuitDesignStep from './CircuitDesignStep';
import CommonStep from './CommonStep';
import ConsultingStartMethodStep from '@routes/NewRequest/ConsultingStartMethodStep';

const PCBStepRequestPresenter = () => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('sm'));
  const [buttonStateHandler, setButtonStateHandler] = useState<{
    disableHandler: (value: boolean) => void;
  }>({
    disableHandler: (value) => undefined
  });
  const currentEventState = useRef<{
    selectedProcess: string;
    exitStage: string;
  }>({
    selectedProcess: '',
    exitStage: ''
  });
  const nextButtonAction = useRef<
    Partial<Record<PcbRequestStageEnum, () => void>>
  >({
    PROCESS_SELECT: () => undefined,
    DESIGN: () => undefined,
    FABRICATION: () => undefined,
    ASSEMBLY: () => undefined,
    PROJECT_COMMON: () => undefined,
    PREVIEW: () => undefined
  });
  const {
    query: { process, stage },
    onAddQueryString
  } = useQueryString<RequestQueryString & { stage: PcbRequestStageEnum }>();
  const history = useHistory<{ fromLandingPage?: boolean }>();
  const track = useTrack();
  const processArray = useMemo(() => process?.split(',') ?? [], [process]);
  const currentStage = useRef<PcbRequestStageEnum | null>(null);
  const lastStage = useRef<PcbRequestStageEnum | null>(null);
  const currentProcess = useRef<string | null>(null);
  const buttonAction = nextButtonAction.current?.[stage as PcbRequestStageEnum];
  const steps = useMemo(() => {
    const stepPrefix = [PcbRequestStageEnum.PROCESS_SELECT];
    const stepSuffix = [
      PcbRequestStageEnum.PROJECT_COMMON,
      PcbRequestStageEnum.CONSULTING_START_METHOD,
      PcbRequestStageEnum.PREVIEW,
      PcbRequestStageEnum.COMPLETE
    ];
    const pcbStepOrder = [
      CapaPcbServiceType.CircuitDesign,
      CapaPcbServiceType.CircuitFabrication,
      CapaPcbServiceType.CircuitAssembly
    ];
    const stepContents: PcbRequestStageEnum[] = [];

    pcbStepOrder.forEach((serviceType) => {
      if (processArray.includes(serviceType)) {
        switch (serviceType) {
          case CapaPcbServiceType.CircuitDesign:
            stepContents.push(PcbRequestStageEnum.DESIGN);
            break;
          case CapaPcbServiceType.CircuitFabrication:
            stepContents.push(PcbRequestStageEnum.FABRICATION);
            break;
          case CapaPcbServiceType.CircuitAssembly: {
            const isAssemblyOnly = processArray.length === 1;
            if (isAssemblyOnly) {
              stepContents.push(PcbRequestStageEnum.FABRICATION);
            }
            stepContents.push(PcbRequestStageEnum.ASSEMBLY);
            break;
          }
        }
      }
    });

    return [...stepPrefix, ...stepContents, ...stepSuffix];
  }, [processArray]);

  const currentStageIndex = steps.findIndex((step) => step === stage);
  const currentStep = useMemo(() => {
    switch (stage) {
      case PcbRequestStageEnum.PROCESS_SELECT:
        return {
          content: (
            <PCBProcessSelect
              setDisabledNextButton={buttonStateHandler.disableHandler}
              nextButtonAction={nextButtonAction}
            />
          ),
          title: '필요한 작업을 모두 선택해주세요'
        };

      case PcbRequestStageEnum.DESIGN:
        return {
          content: (
            <CuircuitDesignStep
              setDisabledNextButton={buttonStateHandler.disableHandler}
              nextButtonAction={nextButtonAction}
            />
          ),
          title: `설계 정보를
              입력해주세요`
        };

      case PcbRequestStageEnum.FABRICATION:
        return {
          content: (
            <PCBProduceStep
              setDisabledNextButton={buttonStateHandler.disableHandler}
              nextButtonAction={nextButtonAction}
            />
          ),
          title: `제작 정보를
              입력해주세요`
        };

      case PcbRequestStageEnum.ASSEMBLY:
        return {
          content: (
            <PCBAssembleStep
              setDisabledNextButton={buttonStateHandler.disableHandler}
              nextButtonAction={nextButtonAction}
            />
          ),
          title: `조립 정보를
                입력해주세요`
        };

      case PcbRequestStageEnum.PROJECT_COMMON:
        return {
          content: (
            <CommonStep
              setDisabledNextButton={buttonStateHandler.disableHandler}
              nextButtonAction={nextButtonAction}
            />
          ),
          title: `프로젝트 정보를
                입력해주세요`
        };

      case PcbRequestStageEnum.CONSULTING_START_METHOD:
        return {
          content: (
            <ConsultingStartMethodStep
              setDisabledNextButton={buttonStateHandler.disableHandler}
              nextButtonAction={nextButtonAction}
            />
          ),
          title: `상담 시작 방식을 선택해주세요.`
        };

      case PcbRequestStageEnum.PREVIEW:
        return {
          content: (
            <RequestPreview
              setDisabledNextButton={buttonStateHandler.disableHandler}
              nextButtonAction={nextButtonAction}
            />
          ),
          title: `마지막으로 요청서를 확인해주세요.`,
          formId: 'preview'
        };
    }
  }, [stage, buttonStateHandler.disableHandler]);

  const extraDescription = useMemo(() => {
    if (!processArray) return;
    if (processArray) {
      const pcbOrder: string[] = [
        CapaPcbServiceType.CircuitDesign,
        CapaPcbServiceType.CircuitFabrication,
        CapaPcbServiceType.CircuitAssembly
      ];

      return pcbOrder
        .reduce((acc, service) => {
          if (processArray.includes(service)) {
            acc.push(requestEnumToString(service as CapaPcbServiceType));
          }
          return acc;
        }, [] as string[])
        .join(', ');
    }
  }, [processArray]);

  const onMoveNextStage = useCallback(() => {
    if (
      stage !== PcbRequestStageEnum.PREVIEW &&
      stage !== PcbRequestStageEnum.COMPLETE
    ) {
      onAddQueryString({
        stage: steps[currentStageIndex + 1]
      });
    }

    buttonAction && buttonAction();
  }, [onAddQueryString, currentStageIndex, steps, buttonAction, stage]);

  const onMovePrevStage = useCallback(() => {
    if (currentStageIndex === 0) {
      history.push('/request');
    } else {
      onAddQueryString({
        stage: steps[currentStageIndex - 1]
      });
    }

    if (stage !== PcbRequestStageEnum.PREVIEW) {
      buttonAction && buttonAction();
    }
  }, [
    steps,
    onAddQueryString,
    currentStageIndex,
    history,
    stage,
    buttonAction
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (stage) {
      currentStage.current = stage;
    }
  }, [stage]);

  useEffect(() => {
    if (process) {
      currentProcess.current = process;
    }
  }, [process]);

  useEffect(() => {
    currentEventState.current = {
      exitStage: !lastStage.current
        ? ('' as PcbRequestStageEnum)
        : lastStage.current,
      selectedProcess: (currentProcess.current ?? '').replace(/CIRCUIT_/g, '')
    };
  }, [process, stage]);

  useEffect(() => {
    if (stage) {
      if (!lastStage.current) {
        lastStage.current = stage;
      }

      if (
        steps.findIndex((step) => step === stage) >
        steps.findIndex((step) => step === lastStage.current)
      ) {
        lastStage.current = stage;
      }
    }
  }, [stage, steps]);

  useEffect(() => {
    const current = currentStage.current;

    return () => {
      if (
        current &&
        current !== PcbRequestStageEnum.COMPLETE &&
        lastStage.current !== PcbRequestStageEnum.COMPLETE
      ) {
        track({
          key: 'exit',
          properties: {
            exitStage: lastStage.current ?? '',
            selectedProcess: (currentProcess.current ?? '').replace(
              /CIRCUIT_/g,
              ''
            )
          }
        });
      }
    };
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    const current = currentStage.current;

    const sendTrackingData = () => {
      if (
        current &&
        current !== PcbRequestStageEnum.COMPLETE &&
        lastStage.current !== PcbRequestStageEnum.COMPLETE
      ) {
        track({
          key: 'exit',
          properties: {
            exitStage: lastStage.current ?? '',
            selectedProcess: (currentProcess.current ?? '').replace(
              /CIRCUIT_/g,
              ''
            )
          }
        });
      }
    };

    window.addEventListener('beforeunload', sendTrackingData);

    return () => {
      window.removeEventListener('beforeunload', sendTrackingData);
    };
  });

  return (
    <RequestCommonLayout
      matches={matches}
      title={currentStep?.title}
      serviceExtraDescription={
        stage !== PcbRequestStageEnum.PROCESS_SELECT
          ? extraDescription
          : undefined
      }
      titleBackgroundImage={asset.ServiceType_Pcb}
      onClickClose={() => history.push('/request')}
    >
      <PannelLayout
        content={currentStep?.content}
        bottomNav={
          stage && (
            <RequestBottomNav
              stage={stage}
              setButtonStateHandler={setButtonStateHandler}
              onClickBack={debounce(onMovePrevStage, 50)}
              onClickNext={debounce(onMoveNextStage, 50)}
              steps={steps}
            />
          )
        }
      />
    </RequestCommonLayout>
  );
};

export default PCBStepRequestPresenter;
